import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { 
  Loader2,
  Search,
  UserCircle,
  Phone,
  AlertTriangle,
  ChevronRight,
  Filter,
  Leaf
} from 'lucide-react';
import { OrgFilter } from '../../components/ui/org-filter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const skinTypeLabels = {
  normal: 'Normal',
  oily: 'Oily',
  dry: 'Dry',
  combination: 'Combination',
  sensitive: 'Sensitive',
};

const pregnancyLabels = {
  not_applicable: 'N/A',
  pregnant: 'Pregnant',
  lactating: 'Lactating',
  trying_to_conceive: 'TTC',
};

const concernLabels = {
  acne: 'Acne',
  pigmentation: 'Pigmentation',
  aging: 'Aging',
  rosacea: 'Rosacea',
  eczema: 'Eczema',
  psoriasis: 'Psoriasis',
  hair_loss: 'Hair Loss',
  dandruff: 'Dandruff',
  dry_skin: 'Dry Skin',
  oily_skin: 'Oily Skin',
  dark_circles: 'Dark Circles',
  scars: 'Scars',
  stretch_marks: 'Stretch Marks',
  fungal: 'Fungal',
  other: 'Other',
};

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function PatientsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [totalPatients, setTotalPatients] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [skinTypeFilter, setSkinTypeFilter] = useState('all');
  const [concernFilter, setConcernFilter] = useState('all');
  const [diagnosisSearch, setDiagnosisSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState(null);
  const [options, setOptions] = useState({ skin_types: [], concern_tags: [], genders: [], pregnancy_statuses: [] });
  
  const PAGE_SIZE = 50;
  
  const fetchPatients = useCallback(async (pageNum = 1, append = false) => {
    if (pageNum === 1) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pageNum);
      params.append('page_size', PAGE_SIZE);
      if (searchTerm) params.append('search', searchTerm);
      if (skinTypeFilter !== 'all') params.append('skin_type', skinTypeFilter);
      if (concernFilter !== 'all') params.append('concern', concernFilter);
      if (diagnosisSearch.trim()) params.append('diagnosis', diagnosisSearch.trim());
      if (orgFilter) params.append('org', orgFilter);
      
      const { data } = await axios.get(`${API_URL}/api/patients?${params}`, { withCredentials: true });
      const items = data.items || data;
      const total = data.total ?? items.length;
      const more = data.has_more ?? false;
      
      if (append) {
        setPatients(prev => [...prev, ...items]);
      } else {
        setPatients(items);
      }
      setTotalPatients(total);
      setHasMore(more);
      setPage(pageNum);
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to fetch patients');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [searchTerm, skinTypeFilter, concernFilter, diagnosisSearch, orgFilter]);

  const loadMore = () => {
    if (hasMore && !loadingMore) fetchPatients(page + 1, true);
  };

  const fetchOptions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/patients/options`, { withCredentials: true });
      setOptions(data);
    } catch (error) {
      console.error('Failed to fetch options', error);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
    fetchOptions();
  }, [fetchPatients, fetchOptions]);

  const basePath = user?.role === 'admin' ? '/admin' : user?.role === 'counsellor' ? '/counsellor' : '/doctor';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="patients-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="patients-list-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-medium tracking-tight text-foreground">
            Patients
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage patient profiles and view their journey
          </p>
        </div>
        <div className="flex items-center gap-3">
          <OrgFilter value={orgFilter} onChange={setOrgFilter} />
          <Badge variant="outline" className="text-xs gap-1 border-emerald-200 bg-emerald-50 text-emerald-700 py-1 px-2">
          <Leaf className="w-3 h-3" /> Source: Eden Appointment System
        </Badge>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <UserCircle className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{totalPatients.toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Total Patients</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-info/10">
              <UserCircle className="w-5 h-5 text-info" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{patients.filter(p => p.is_active).length}</p>
              <p className="text-xs text-muted-foreground">Active</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-warning/10">
              <AlertTriangle className="w-5 h-5 text-warning" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{patients.filter(p => p.allergies?.length > 0).length}</p>
              <p className="text-xs text-muted-foreground">With Allergies</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-secondary/10">
              <UserCircle className="w-5 h-5 text-secondary" />
            </div>
            <div>
              <p className="text-2xl font-heading font-semibold">{patients.filter(p => p.pregnancy_status !== 'not_applicable').length}</p>
              <p className="text-xs text-muted-foreground">Special Care</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-border shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 rounded-xl"
                data-testid="search-patients-input"
              />
            </div>
            <Select value={skinTypeFilter} onValueChange={setSkinTypeFilter}>
              <SelectTrigger className="w-full sm:w-40 rounded-xl" data-testid="skin-type-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Skin Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Skin Types</SelectItem>
                {options.skin_types?.map((type) => (
                  <SelectItem key={type} value={type}>{skinTypeLabels[type] || type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={concernFilter} onValueChange={setConcernFilter}>
              <SelectTrigger className="w-full sm:w-40 rounded-xl" data-testid="concern-filter">
                <SelectValue placeholder="Concern" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Concerns</SelectItem>
                {options.concern_tags?.map((concern) => (
                  <SelectItem key={concern} value={concern}>{concernLabels[concern] || concern}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Patient List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {patients.length === 0 ? (
          <Card className="col-span-full border-border shadow-sm">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <UserCircle className="w-12 h-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground text-center">
                {searchTerm ? 'No patients found matching your search' : 'No patients registered yet'}
              </p>
            </CardContent>
          </Card>
        ) : (
          patients.map((patient) => (
            <Card 
              key={patient.id} 
              className="border-border shadow-sm card-interactive cursor-pointer"
              onClick={() => navigate(`/patients/${patient.id}`)}
              data-testid={`patient-card-${patient.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-primary font-heading font-semibold text-lg">
                        {patient.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-heading font-medium">{patient.name}</h3>
                        {patient.eden_patient_id && (
                          <Badge className="h-4 px-1.5 text-[9px] bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100 gap-0.5">
                            <Leaf className="w-2 h-2" /> Eden
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {patient.age} yrs &bull; {patient.gender === 'male' ? 'M' : patient.gender === 'female' ? 'F' : 'O'}
                      </p>
                    </div>
                  </div>
                  {patient.pregnancy_status !== 'not_applicable' && (
                    <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20 text-xs">
                      {pregnancyLabels[patient.pregnancy_status]}
                    </Badge>
                  )}
                </div>
                
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <Phone className="w-4 h-4" />
                  <span>{patient.phone}</span>
                </div>
                
                {patient.allergies?.length > 0 && (
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-4 h-4 text-destructive" />
                    <span className="text-sm text-destructive">{patient.allergies.join(', ')}</span>
                  </div>
                )}
                
                <div className="flex items-center justify-between pt-3 border-t border-border">
                  <div className="flex flex-wrap gap-1">
                    <Badge variant="outline" className="text-xs">
                      {skinTypeLabels[patient.skin_type]}
                    </Badge>
                    {patient.concern_tags?.slice(0, 2).map(tag => (
                      <Badge key={tag} variant="secondary" className="text-xs">
                        {concernLabels[tag] || tag}
                      </Badge>
                    ))}
                    {patient.concern_tags?.length > 2 && (
                      <Badge variant="secondary" className="text-xs">
                        +{patient.concern_tags.length - 2}
                      </Badge>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Load More */}
      {hasMore && (
        <div className="flex justify-center py-4" data-testid="load-more-patients">
          <Button
            variant="outline"
            onClick={loadMore}
            disabled={loadingMore}
            className="rounded-full px-6 gap-2"
          >
            {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Load More ({patients.length} of {totalPatients.toLocaleString()})
          </Button>
        </div>
      )}
      {!hasMore && patients.length > 0 && totalPatients > PAGE_SIZE && (
        <p className="text-center text-xs text-muted-foreground py-2">
          Showing all {totalPatients.toLocaleString()} patients
        </p>
      )}
    </div>
  );
}
